// src/v2/hooks/useUiPrefs.js
//
// Reusable hook: persists an arbitrary state object to localStorage.
// Semantics: survives tab close and browser restart.
//
// API:
//   const [state, setState, clearState] = useUiPrefs(key, defaultState);
//
//   state       — current state (restored from localStorage on mount, or defaultState)
//   setState    — write new state (callers are responsible for merging)
//   clearState  — resets to defaultState and removes the localStorage entry
//
// Versioning convention: embed a version suffix in the key constant
// (e.g. "ui_prefs_leases_v1") so bumping the suffix resets all users to
// defaults for that page when a breaking state-shape change ships.
//
// URL-param precedence: handled entirely by the caller. This hook is pure
// localStorage read/write; it does not inspect URL params.

import { useCallback, useRef, useState } from "react";
import { log } from "../../utils/logger.js";

const identity = (state) => state;

export function useUiPrefs(storageKey, defaultState, migrate = identity) {
  const defaultRef = useRef(migrate(defaultState));

  const [state, setStateInternal] = useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === null) {
        // Nothing stored yet — persist defaults so callers can read current state.
        try { window.localStorage.setItem(storageKey, JSON.stringify(defaultRef.current)); } catch { /* ignore */ }
        return defaultRef.current;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return defaultRef.current;
      return migrate(parsed);
    } catch (err) {
      log.error(`useUiPrefs: load failed key=${storageKey}`, err);
      return defaultRef.current;
    }
  });

  const setState = useCallback(
    (updater) => {
      setStateInternal((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch (err) {
          log.error(`useUiPrefs: save failed key=${storageKey}`, err);
        }
        return next;
      });
    },
    [storageKey],
  );

  const clearState = useCallback(() => {
    try {
      window.localStorage.removeItem(storageKey);
    } catch (err) {
      log.error(`useUiPrefs: clear failed key=${storageKey}`, err);
    }
    setStateInternal(defaultRef.current);
  }, [storageKey]);

  return [state, setState, clearState];
}
